import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * InfoTip (design system v1): a 16px "i" next to a label, heading, column
 * header or KPI that opens a short explanation in a small inverted bubble.
 *
 * Rule: an explanation longer than about three words goes into an InfoTip
 * next to its label, never in brackets or grey text beside it. EXCEPTION:
 * text whose absence could cause a mistake or a loss (consequences of an
 * action, privacy promises, validation errors, empty-state reasons) and
 * subheadlines stay visible.
 *
 * Opens on hover, keyboard focus and tap (toggle, for touch); Esc and an
 * outside tap close it. The bubble is role="tooltip" and describes the button.
 * Clicks don't bubble, so it is safe inside clickable rows, cards and labels.
 *
 * The bubble is portalled to <body> and positioned with `position: fixed` from the
 * icon's rectangle, so a scrolling table box (overflow-x-auto), a card or any other
 * clipping parent can never cut it off. `side` / `align` are the PREFERRED placement:
 * the bubble flips to the other side when there is no room and is kept inside the
 * viewport (the arrow still points at the icon).
 */

const EDGE = 8; // min gap between the bubble and the viewport edge, px
const GAP = 8; // gap between the icon and the bubble, px

type Placed = { left: number; top: number; arrowLeft: number; side: 'top' | 'bottom' };

export function InfoTip({
  text,
  children,
  label,
  side = 'top',
  align = 'center',
  tone = 'surface',
  className,
}: {
  /** The explanation (at most ~3 sentences). `children` wins if both are set. */
  text?: string;
  children?: ReactNode;
  /** What the tip explains, for the accessible name ("About {label}"). */
  label?: string;
  side?: 'top' | 'bottom';
  /** Which edge of the bubble lines up with the icon; use start/end near screen edges. */
  align?: 'center' | 'start' | 'end';
  /** 'surface' on cards (default); 'page' for text sitting directly on today's dark app page. */
  tone?: 'surface' | 'page';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [placed, setPlaced] = useState<Placed | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const tipId = useId();

  const close = () => { setOpen(false); setPinned(false); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  // Measure the icon and the bubble, pick a side that fits, keep it inside the viewport.
  const place = useCallback(() => {
    const btn = btnRef.current;
    const bubble = bubbleRef.current;
    if (!btn || !bubble) return;
    const r = btn.getBoundingClientRect();
    const w = bubble.offsetWidth;
    const h = bubble.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const fitsTop = r.top - GAP - h >= EDGE;
    const fitsBottom = r.bottom + GAP + h <= vh - EDGE;
    const useSide = side === 'top' ? (fitsTop || !fitsBottom ? 'top' : 'bottom') : (fitsBottom || !fitsTop ? 'bottom' : 'top');
    const centre = r.left + r.width / 2;
    const wanted = align === 'center' ? centre - w / 2 : align === 'start' ? r.left - GAP : r.right + GAP - w;
    const left = Math.max(EDGE, Math.min(wanted, vw - w - EDGE));
    const top = useSide === 'top' ? r.top - GAP - h : r.bottom + GAP;
    const arrowLeft = Math.max(10, Math.min(centre - left, w - 10));
    setPlaced({ left, top, arrowLeft, side: useSide });
  }, [side, align]);

  useLayoutEffect(() => {
    if (!open) { setPlaced(null); return; }
    place();
  }, [open, place, text, children]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const onPage = tone === 'page';
  const bubble = onPage ? 'bg-surface text-fg' : 'bg-tip-bg text-tip-text';
  const shownSide = placed?.side ?? side;
  const arrow = shownSide === 'top'
    ? `top-full ${onPage ? 'border-t-surface' : 'border-t-tip-bg'}`
    : `bottom-full ${onPage ? 'border-b-surface' : 'border-b-tip-bg'}`;
  const glyph = open
    ? (onPage ? 'border-body text-body' : 'border-fg text-fg')
    : (onPage ? 'border-faint text-faint hover:border-body hover:text-body' : 'border-fg-faint text-fg-faint hover:border-fg hover:text-fg');

  return (
    <span
      ref={wrapRef}
      className={`relative inline-flex align-middle ${className ?? ''}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => { if (!pinned) setOpen(false); }}
    >
      <button
        ref={btnRef}
        type="button"
        aria-label={label ? `About ${label}` : 'More information'}
        aria-describedby={open ? tipId : undefined}
        onFocus={() => setOpen(true)}
        // Leaving by keyboard closes it even when pinned (no orphaned bubble).
        onBlur={(e) => { if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) close(); }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (pinned) close();
          else { setPinned(true); setOpen(true); }
        }}
        // 16px glyph; the ::before grows the touch target to 40px.
        className={`relative inline-flex items-center justify-center w-4 h-4 p-0 rounded-full border bg-transparent text-2xs !tracking-normal leading-none font-semibold font-sans cursor-help select-none transition-colors duration-fast before:absolute before:-inset-3 before:content-[''] ${glyph}`}
      >
        i
      </button>
      {open && createPortal(
        <span
          ref={bubbleRef}
          id={tipId}
          role="tooltip"
          // Carry a local theme override (data-theme="light" surfaces) over to the portalled bubble.
          data-theme={wrapRef.current?.closest('[data-theme]')?.getAttribute('data-theme') ?? undefined}
          style={{
            position: 'fixed',
            left: placed?.left ?? 0,
            top: placed?.top ?? 0,
            // Hidden for the one frame in which the bubble is measured.
            visibility: placed ? 'visible' : 'hidden',
          }}
          className={`z-tooltip w-max max-w-infotip rounded-md px-2.5 py-1.5 text-xs leading-snug font-normal font-sans normal-case tracking-normal text-left whitespace-normal shadow-md pointer-events-none ${bubble}`}
        >
          {children ?? text}
          <span
            aria-hidden="true"
            style={{ left: placed?.arrowLeft ?? 0, transform: 'translateX(-50%)' }}
            className={`absolute ${arrow} border-[5px] border-transparent`}
          />
        </span>,
        document.body,
      )}
    </span>
  );
}
